import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { getCreationRequestBatches, getCreationRequestLinks } from '../../api/url_data_agent_evaluations';
import { ADMIN_SCOPES } from '../../constants/admin_scope_constants';
import { hasScope } from '../../helpers/scope_helpers';
import {
  CREATION_LINK_STATES,
  getBatchModeLabel,
  getCreationCountKey,
  getCreationReasonCopy,
  getLaneStopNote
} from '../../helpers/url_data_agent_creation_requests_helpers';
import { formatDate, formatNumber } from '../../helpers/url_data_agent_evaluations_helpers';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';
import CreationRequestStartForm from './CreationRequestStartForm';
import SilentEvalCreationRequestLinks from './SilentEvalCreationRequestLinks';

import './SilentEvalCreationRequests.scss';

const URL_DEFAULTS = { batch: '' };
const COUNT_COLUMNS = CREATION_LINK_STATES.map(state => ({ key: getCreationCountKey(state.value), label: state.label }));
const TOP_REASONS = 3;
const COUNTS_FAILED_NOTE = 'Counting this batch’s links took too long on the database. Open it to try again.';

// The catalog team's creation batches with where each stands; opening one lists its links.
const SilentEvalCreationRequests = ({ active = true }) => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const currentUser = useSelector(state => state.users.currentUser);
  const canStartBatches = hasScope(currentUser?.scopes || [], ADMIN_SCOPES.EDIT_PRODUCT);
  const tag = urlState.batch;
  const [result, setResult] = useState(null);
  const [countsByTag, setCountsByTag] = useState({});
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const countsRef = useRef({});
  const isListOnScreen = active && !tag && !isStarting;
  const batches = (result?.batches || []).map(batch => ({ ...batch, ...countsByTag[batch.tag] }));
  const laneStopNote = getLaneStopNote(result?.lane);

  useEffect(() => {
    if (!isListOnScreen) return undefined;
    let cancelled = false;
    setIsFetching(true);
    setError(null);
    getCreationRequestBatches()
      .then(data => {
        if (!cancelled) setResult(data);
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isListOnScreen, refreshKey]);

  // Each batch's counts group its links on the replica, so they load one batch at a time after the list is on screen;
  // a batch whose count is cut off shows a dash until it is opened.
  useEffect(() => {
    if (!isListOnScreen || !result) return undefined;
    const pending = result.batches.map(batch => batch.tag).filter(batchTag => !countsRef.current[batchTag]);
    if (!pending.length) return undefined;
    let cancelled = false;
    const record = (batchTag, counts) => {
      countsRef.current = { ...countsRef.current, [batchTag]: counts };
      setCountsByTag(countsRef.current);
    };
    pending.forEach(batchTag => record(batchTag, { status: 'loading' }));
    (async () => {
      for (const batchTag of pending) {
        if (cancelled) return;
        try {
          const data = await getCreationRequestLinks(batchTag, { countOnly: 1 });
          if (!cancelled) record(batchTag, { status: 'ready', counts: data.links, refusals: data.reasons.needsPerson });
        } catch {
          if (!cancelled) record(batchTag, { status: 'failed' });
        }
      }
    })();
    return () => {
      cancelled = true;
      pending.filter(batchTag => countsRef.current[batchTag]?.status === 'loading').forEach(batchTag => record(batchTag, undefined));
    };
  }, [isListOnScreen, result]);

  const refresh = () => {
    countsRef.current = {};
    setCountsByTag({});
    setRefreshKey(key => key + 1);
  };

  const loadMore = async () => {
    setIsLoadingMore(true);
    try {
      const data = await getCreationRequestBatches({ offset: result.batches.length });
      setResult(previous => ({ ...previous, ...data, batches: [...previous.batches, ...data.batches] }));
    } catch (err) {
      window.ALERT.error(`Could not load more batches: ${err.message}`);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const closeStartForm = () => {
    setIsStarting(false);
    refresh();
  };

  const openStartedBatch = startedTag => {
    closeStartForm();
    patchUrl({ batch: startedTag });
  };

  if (tag) {
    return <SilentEvalCreationRequestLinks tag={tag} active={active} onBack={() => patchUrl({ batch: '', batchState: '' })} />;
  }

  if (isStarting) {
    return <CreationRequestStartForm batches={result.batches} lane={result.lane} onClose={closeStartForm} onOpenBatch={openStartedBatch} />;
  }

  return (
    <div className='silent-eval-creation-requests'>
      <div className='se-toolbar'>
        {canStartBatches && (
          <button type='button' className='se-btn primary' disabled={!result} onClick={() => setIsStarting(true)}>
            New batch
          </button>
        )}
        <button type='button' className='se-btn' disabled={isFetching} onClick={refresh}>
          Refresh
        </button>
        <span className={cn('se-refreshing', { on: isFetching })} aria-hidden='true' />
      </div>
      <p className='cr-intro'>
        Pins the catalog team confirmed are missing from the catalog. APM reads each link’s page and prepares its product, and creates it once a person
        approves, or as soon as it is ready in a Create when ready batch. Link counts fill in one batch at a time.
      </p>

      {laneStopNote && (
        <div className='cr-lane-stopped' role='status'>
          {laneStopNote}
        </div>
      )}
      {error && (
        <div className='cr-error' role='alert'>
          Could not {result ? 'refresh' : 'load'} the batches — {error}
        </div>
      )}
      {!result ? (
        !error && (
          <div className='se-empty' role='status'>
            Loading batches…
          </div>
        )
      ) : !batches.length ? (
        <div className='se-empty'>No creation batches yet.</div>
      ) : (
        <div className='cr-list' role='table' aria-label='Creation batches'>
          <div className='cr-head' role='row'>
            <span role='columnheader'>Batch</span>
            <span role='columnheader'>Requester</span>
            <span role='columnheader'>Started</span>
            <span role='columnheader'>Mode</span>
            <span role='columnheader'>Links</span>
            {COUNT_COLUMNS.map(column => (
              <span key={column.key} role='columnheader'>
                {column.label}
              </span>
            ))}
            <span role='columnheader'>Top refusal reasons</span>
          </div>
          {batches.map(batch => {
            const counts = batch.status === 'ready' ? batch.counts : null;
            return (
              <button
                key={batch.tag}
                type='button'
                className='cr-row'
                role='row'
                aria-label={`Open batch ${batch.name}`}
                aria-busy={batch.status === 'loading'}
                onClick={() => patchUrl({ batch: batch.tag })}
              >
                <span className='cr-cell cr-name' role='cell'>
                  <span className='cr-title'>{batch.name}</span>
                  {batch.sop && <span className='cr-sub'>{batch.sop}</span>}
                </span>
                <span className='cr-cell' role='cell'>
                  {batch.requester?.name || '—'}
                </span>
                <span className='cr-cell' role='cell'>
                  {formatDate(batch.firstParkedAt)}
                </span>
                <span className='cr-cell' role='cell'>
                  <span className={cn('cr-mode', { 'create-when-ready': batch.createWhenReady })}>{getBatchModeLabel(batch)}</span>
                </span>
                <span className='cr-cell num' role='cell'>
                  {formatNumber(batch.links)}
                </span>
                {COUNT_COLUMNS.map(column => (
                  <span
                    key={column.key}
                    className={cn('cr-cell num', { zero: counts && !counts[column.key], pending: !counts, ready: column.key === 'ready' })}
                    role='cell'
                    title={batch.status === 'failed' ? COUNTS_FAILED_NOTE : undefined}
                  >
                    {counts ? formatNumber(counts[column.key]) : batch.status === 'failed' ? '—' : '…'}
                  </span>
                ))}
                <span className='cr-cell cr-reasons' role='cell'>
                  {(batch.refusals || []).slice(0, TOP_REASONS).map(({ reason, count }) => (
                    <span key={reason} className='cr-reason' title={getCreationReasonCopy(reason)}>
                      {formatNumber(count)} {getCreationReasonCopy(reason)}
                    </span>
                  ))}
                </span>
              </button>
            );
          })}
          {result.hasMore && (
            <button type='button' className='se-btn cr-load-more' disabled={isLoadingMore} onClick={loadMore}>
              {isLoadingMore ? 'Loading…' : `Load more (${formatNumber(result.total - result.batches.length)} left)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

SilentEvalCreationRequests.propTypes = {
  active: PropTypes.bool
};

export default SilentEvalCreationRequests;
